<script setup lang="ts">
import { computed, ref, watch, provide, onBeforeUnmount } from "vue";
import type { Component } from "vue";
import SearchNav from "../SearchNav/SearchNav.vue";
import PackageSearchForm from "../SearchForm/PackageSearchForm.vue";
import HotelSearchForm from "../SearchForm/HotelSearchForm.vue";
import type { SearchType } from "../../../types/tabs";
import { ModifySearchMode } from "../../../types/common";
import type { SearchSpaConfig } from "../../../types/search-spa-config";
import { onClickOutside } from "@vueuse/core";
import { showSpaOverlay, hideSpaOverlay } from "../../utils/spa-overlay";
import { dataLayerSearchWidgetExpand } from "../../analytics/package-search-form";

export interface Props {
  searchSpaConfig: SearchSpaConfig;
}

const props = defineProps<Props>();

const componentMap: Record<SearchType, Component | null> = {
  packages: PackageSearchForm,
  hotels: HotelSearchForm,
  link: null,
  flight_hotel: null,
};

const updateActiveTab = (tabId: string) => {
  activeTab.value = tabId;
};

const getComponent = (searchType: SearchType) => {
  return componentMap[searchType];
};

const activeTab = ref<string>(props.searchSpaConfig.defaultTabId);

watch(
  () => props.searchSpaConfig.defaultTabId,
  (newDefaultTabId) => {
    // the audience changed, need to reset default tab
    activeTab.value = newDefaultTabId;
  }
);

const allLinks = computed((): boolean => {
  return props.searchSpaConfig.filteredTabs.every(
    (tab) => tab.searchType === "link"
  );
});

const showTabs = computed(() => {
  return props.searchSpaConfig.filteredTabs.length > 1;
});

const containerRef = ref();

const activeTabConfig = computed(() => {
  return props.searchSpaConfig.filteredTabs.find(
    (tabConfig) => tabConfig.id === activeTab.value
  );
});

const isModifySearch = ref(activeTabConfig.value?.isModifySearch);

const searchFormMode = ref<ModifySearchMode>(ModifySearchMode.view);

const isUpdating = computed(() => {
  return searchFormMode.value === ModifySearchMode.update;
});

function changeSearchMode(mode: ModifySearchMode) {
  searchFormMode.value = mode;
  if (mode === ModifySearchMode.update) {
    showSpaOverlay();
    enableClickOutside();
    enableEscapeKey();
  } else {
    hideSpaOverlay();
    disableEscapeKey();
  }

  dataLayerSearchWidgetExpand(mode === ModifySearchMode.update);
}

function escapeKeyListener(e: KeyboardEvent) {
  if (e.key === "Escape") {
    exitOverlay();
  }
}

function enableEscapeKey() {
  window.addEventListener("keydown", escapeKeyListener);
}
function disableEscapeKey() {
  window.removeEventListener("keypress", escapeKeyListener);
}

function exitOverlay() {
  changeSearchMode(ModifySearchMode.view);
  disableEscapeKey();
}

const clickOutsideEnabled = ref(false);
function enableClickOutside() {
  if (!clickOutsideEnabled.value) {
    clickOutsideEnabled.value = true;
    onClickOutside(containerRef, exitOverlay);
  }
}

function updateSearchHandler() {
  changeSearchMode(ModifySearchMode.update);
}
window.addEventListener("updatePackagesSearch", updateSearchHandler);

onBeforeUnmount(() => {
  window.removeEventListener("updatePackagesSearch", updateSearchHandler);
});

window.addEventListener("vacvMobileOverlay-close", () => {
  const maincontent = document.getElementById("maincontent");
  if (maincontent) {
    maincontent.style.zIndex = "";
  }
});
window.addEventListener("vacvMobileOverlay-open", () => {
  const maincontent = document.getElementById("maincontent");
  if (maincontent) {
    maincontent.style.zIndex = "2000";
  }
});

// Avoiding prop drilling. Used in BaseSearchForm component
provide("activeTab", activeTab);
provide("audience", props.searchSpaConfig.audience);
provide("containerRef", containerRef);
provide("searchFormMode", searchFormMode);
</script>

<template>
  <div
    :class="{
      'container-fullbleed': true,
      'is-search-modify': isModifySearch,
      'is-updating': isUpdating,
    }"
    ref="containerRef"
  >
    <section class="search-container vacv-container">
      <search-nav
        :tabs="searchSpaConfig.filteredTabs"
        :audience="searchSpaConfig.audience"
        @select="updateActiveTab"
        :activeTab="activeTab"
        v-if="showTabs"
      />
      <div class="search-content-wrapper" v-if="!allLinks && containerRef">
        <template v-for="tab in searchSpaConfig.filteredTabs">
          <component
            v-if="activeTab === tab.id"
            :is="getComponent(tab.searchType)"
            :tabConfig="tab"
            :productType="tab.label.en"
            :key="tab.id"
            @search-mode="changeSearchMode"
          >
          </component>
        </template>
      </div>
    </section>
  </div>
</template>

<style lang="scss">
@use "vacv-components/themes/acv-default/default-includes" as *;

.vacv-spa {
  // DEFAULT search-container (NOT a child of .is-search-modify, which is Modify Search bar in sub-pages)

  div:not(.is-search-modify) > .search-container {
    margin-bottom: 30px;
    background-color: $col-white;
    box-shadow: $box-shadow-bottom;
    border-radius: 15px;
  }

  .search-container {
    z-index: 102; // fix issue with footer

    .search-content-wrapper {
      padding: 15px;

      .search-content-panel {
        z-index: 2;
        position: relative;
      }
    }
  }

  // MODIFY SEARCH BAR
  .is-search-modify {
    background-color: $col-greylt;

    // initial state (not updating with search inputs)
    &:not(.is-updating) {
      .search-container .vacv-form-row.vacv-input.wide-5-items {
        @media #{$desktop-md} {
          justify-content: flex-start;
          align-items: center;
        }

        &.hotels {
          @media #{$tablet-sm} {
            > .vacv-form-item {
              &.type {
                width: auto;
              }

              &.location {
                width: 75%;
                margin-left: 30px;
              }
            }
          }
        }

        > .vacv-form-item {
          margin-bottom: 15px;

          @media #{$desktop-md} {
            flex: unset;
            margin-right: 0;
            margin-bottom: 0;

            &:not(:first-child) {
              margin-left: 30px;
            }
          }

          &.type {
            @include font-med19;
          }

          @media #{$desktop-md} {
            &.location {
              max-width: 300px;
            }
          }

          &.guests-rooms {
            &::before {
              content: "|";
              margin-right: 20px;
              padding-left: 20px;
            }

            > span {
              display: inline-block;

              &.guests {
                i.fa-user::before {
                  font-size: 1.4rem;
                }
              }

              &.rooms {
                margin-left: 20px;
              }
            }
          }

          @media #{$desktopsm-max} {
            &.dates,
            &.guests-rooms {
              width: auto;
              margin-bottom: 0;
            }
          }

          @media #{$desktop-md} {
            &.dates,
            &.guests-rooms {
              &::before {
                content: "|";
                margin-right: 0;
                padding-right: 30px;
                padding-left: 0;
              }
            }
          }

          &.submit {
            margin-top: 15px;
            margin-bottom: 0;
            text-align: center;

            @media #{$tablet-md} {
              flex: unset;
              position: absolute;
              top: 50%;
              margin-top: 0;
              text-align: right;
              transform: translateY(-50%);
            }

            @media #{$desktop-md} {
              position: relative;
              top: unset;
              margin-left: auto;
              transform: unset;
            }

            button {
              width: unset;
              min-width: 100px;
            }
          }
        }
      }
    }
  }
}
</style>
